import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { NbAuthService } from '@nebular/auth';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AgentContextService } from './agent-context.service';
import { AgentUiService } from './agent-ui.service';
import { AgentApprovalField } from './approval-fields';

export interface AgentThread {
  id: string;
  title: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AgentApprovalAction {
  proposal_id: string;
  action_id?: string | null;
  summary?: string;
  risk?: string;
  fields?: AgentApprovalField[];
  before?: unknown;
  ui_target?: string | null;
  /** `setting.<variable_name>` of a settings write — where „Zeigen" points */
  ui_anchor?: string | null;
}

export interface AgentApproval {
  interruptId: string;
  reason: string;
  actions: AgentApprovalAction[];
  state: 'pending' | 'sending' | 'approved' | 'rejected';
}

export interface AgentTrace {
  ok: boolean;
  actionId: string | null;
  summary: string;
  uiTarget: string | null;
  uiAnchor?: string | null;
  /** why it did not run, e.g. `rejected_by_user` */
  reason?: string | null;
}

export interface AgentChatMessage {
  id: string;
  role: 'user' | 'agent';
  text: string;
  /** the agent is still writing this message */
  streaming?: boolean;
  /** an error notice, not an answer */
  error?: boolean;
  /** the agent waits for the user to approve changes */
  approval?: AgentApproval;
  /** a change the agent made (or that failed) */
  trace?: AgentTrace;
}

export type AgentChatError = 'disabled' | 'busy' | 'failed' | 'network';

const WIDTH_KEY = 'al-agentPanel-width';
export const AGENT_PANEL_WIDTH = { default: 420, min: 320, max: 900 } as const;

/** One parsed Server-Sent Event. */
export interface AgentSseEvent {
  event: string;
  data: unknown;
}

/** Splits a text/event-stream buffer into complete events; returns them and the unfinished rest. */
export function parseSse(buffer: string): { events: AgentSseEvent[]; rest: string } {
  const blocks = buffer.split(/\r?\n\r?\n/);
  const rest = blocks.pop() ?? '';
  const events: AgentSseEvent[] = [];
  for (const block of blocks) {
    let event = 'message';
    const data: string[] = [];
    for (const line of block.split(/\r?\n/)) {
      if (!line || line.startsWith(':')) continue; // keepalive comment
      const colon = line.indexOf(':');
      const field = colon < 0 ? line : line.slice(0, colon);
      const value = colon < 0 ? '' : line.slice(colon + 1).replace(/^ /, '');
      if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
    }
    if (!data.length) continue;
    try {
      events.push({ event, data: JSON.parse(data.join('\n')) });
    } catch {
      // a frame we cannot read is dropped, not shown
    }
  }
  return { events, rest };
}

function clampWidth(width: number): number {
  const viewportMax = typeof window !== 'undefined' ? Math.floor(window.innerWidth * 0.6) : AGENT_PANEL_WIDTH.max;
  return Math.max(AGENT_PANEL_WIDTH.min, Math.min(width, AGENT_PANEL_WIDTH.max, Math.max(AGENT_PANEL_WIDTH.min, viewportMax)));
}

/**
 * The portal panel's conversation with the in-house agent.
 *
 * Root service on purpose: the dashboard layout is mounted separately for the
 * normal and the admin area, and a conversation (and a running answer) must
 * survive moving between them. Talks to al-tutor `/portal-agent/*`; the answer
 * arrives as Server-Sent Events over `fetch` (EventSource cannot send the
 * Authorization header). See features/docs/technical/portal-agent-native.md.
 */
@Injectable({ providedIn: 'root' })
export class AgentChatService {
  private readonly http = inject(HttpClient);
  private readonly nbAuth = inject(NbAuthService);
  private readonly translate = inject(TranslateService);
  private readonly context = inject(AgentContextService);
  private readonly ui = inject(AgentUiService);
  private readonly base = `${environment.tutorsBackend}/portal-agent`;

  readonly enabled = signal(false);
  readonly open = signal(false);
  readonly width = signal(this.storedWidth());
  readonly threads = signal<AgentThread[]>([]);
  readonly threadId = signal<string | null>(null);
  readonly messages = signal<AgentChatMessage[]>([]);
  readonly busy = signal(false);
  readonly loadingThread = signal(false);
  readonly progress = signal<string | null>(null);
  readonly error = signal<AgentChatError | null>(null);

  private abort: AbortController | null = null;
  private counter = 0;

  // ------------------------------------------------------------------ panel

  async loadStatus(): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ data: { enabled: boolean } }>(`${this.base}/status`, {
          headers: { 'X-Suppress-Error-Toast': '1' },
        })
      );
      this.enabled.set(!!res?.data?.enabled);
    } catch {
      this.enabled.set(false);
    }
  }

  toggle(): void {
    if (this.open()) this.close();
    else this.openPanel();
  }

  openPanel(): void {
    if (!this.enabled()) return;
    this.open.set(true);
    if (!this.threads().length) this.loadThreads();
  }

  close(): void {
    this.open.set(false);
  }

  setWidth(width: number, persist = false): void {
    const value = clampWidth(width);
    this.width.set(value);
    if (persist) {
      try {
        localStorage.setItem(WIDTH_KEY, String(value));
      } catch {
        // private mode — the width is a convenience
      }
    }
  }

  resetWidth(): void {
    this.setWidth(AGENT_PANEL_WIDTH.default, true);
  }

  private storedWidth(): number {
    try {
      const value = Number(localStorage.getItem(WIDTH_KEY));
      return value ? clampWidth(value) : AGENT_PANEL_WIDTH.default;
    } catch {
      return AGENT_PANEL_WIDTH.default;
    }
  }

  // ---------------------------------------------------------------- threads

  async loadThreads(): Promise<void> {
    try {
      const res = await firstValueFrom(this.http.get<{ data: AgentThread[] }>(`${this.base}/threads`));
      this.threads.set(res?.data ?? []);
    } catch {
      this.threads.set([]);
    }
  }

  newChat(): void {
    this.stopListening();
    this.threadId.set(null);
    this.messages.set([]);
    this.error.set(null);
  }

  async selectThread(id: string): Promise<void> {
    if (id === this.threadId()) return;
    this.stopListening();
    this.threadId.set(id);
    this.messages.set([]);
    this.error.set(null);
    this.loadingThread.set(true);
    try {
      const res = await firstValueFrom(
        this.http.get<{ data: { role: 'user' | 'agent'; text: string }[] }>(`${this.base}/threads/${id}/messages`)
      );
      if (this.threadId() === id) {
        this.messages.set((res?.data ?? []).map((m) => ({ id: this.nextId(), role: m.role, text: m.text })));
      }
    } catch {
      this.error.set('failed');
    } finally {
      this.loadingThread.set(false);
    }
  }

  async deleteThread(id: string): Promise<void> {
    try {
      await firstValueFrom(this.http.delete(`${this.base}/threads/${id}`));
    } finally {
      if (this.threadId() === id) this.newChat();
      await this.loadThreads();
    }
  }

  // ------------------------------------------------------------------- send

  async send(raw: string): Promise<void> {
    const text = raw.trim();
    if (!text || this.busy()) return;
    this.error.set(null);
    this.busy.set(true);
    this.progress.set(null);
    this.messages.update((list) => [...list, { id: this.nextId(), role: 'user', text }]);
    const answerId = this.nextId();
    this.messages.update((list) => [...list, { id: answerId, role: 'agent', text: '', streaming: true }]);

    const threadId = this.threadId() ?? (await this.createThread().catch(() => null));
    if (!threadId) {
      this.finish(answerId, 'network');
      return;
    }
    await this.stream(`${this.base}/threads/${threadId}/messages`, {
      text,
      context: this.context.snapshot(),
      ui_language: this.translate.currentLang || this.translate.defaultLang,
    }, answerId);
  }

  /** The user approves or rejects every change on an approval card; the agent continues. */
  async decide(messageId: string, approve: boolean): Promise<void> {
    const message = this.messages().find((m) => m.id === messageId);
    const threadId = this.threadId();
    if (!message?.approval || message.approval.state !== 'pending' || !threadId || this.busy()) return;
    this.setApproval(messageId, 'sending');
    this.error.set(null);
    this.busy.set(true);
    const answerId = this.nextId();
    this.messages.update((list) => [...list, { id: answerId, role: 'agent', text: '', streaming: true }]);
    await this.stream(`${this.base}/threads/${threadId}/resume`, {
      interrupt_id: message.approval.interruptId,
      decisions: message.approval.actions.map((a) => ({ proposal_id: a.proposal_id, approve })),
      ui_language: this.translate.currentLang || this.translate.defaultLang,
    }, answerId);
    this.setApproval(messageId, approve ? 'approved' : 'rejected');
  }

  private async stream(url: string, payload: unknown, answerId: string): Promise<void> {
    let failure: AgentChatError | null = null;
    try {
      const token = await this.accessToken();
      this.abort = new AbortController();
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify(payload),
        signal: this.abort.signal,
      });
      if (!response.ok || !response.body) {
        failure = response.status === 403 ? 'disabled' : response.status === 409 ? 'busy' : 'failed';
      } else {
        await this.read(response.body, answerId);
      }
    } catch (err) {
      if ((err as { name?: string })?.name !== 'AbortError') failure = 'network';
    } finally {
      this.finish(answerId, failure);
    }
  }

  private finish(answerId: string, failure: AgentChatError | null): void {
    if (failure) this.fail(answerId, failure);
    this.abort = null;
    this.busy.set(false);
    this.progress.set(null);
    this.patch(answerId, { streaming: false });
    this.messages.update((list) => list.filter((m) => m.id !== answerId || m.text || m.error));
    this.loadThreads();
  }

  private setApproval(messageId: string, state: AgentApproval['state']): void {
    this.messages.update((list) =>
      list.map((m) => (m.id === messageId && m.approval ? { ...m, approval: { ...m.approval, state } } : m))
    );
  }

  /** Stops listening to the answer. The server finishes the run anyway. */
  stopListening(): void {
    this.abort?.abort();
    this.abort = null;
  }

  private async read(body: ReadableStream<Uint8Array>, answerId: string): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parsed = parseSse(buffer);
      buffer = parsed.rest;
      parsed.events.forEach((e) => this.handle(e, answerId));
    }
    parseSse(buffer + '\n\n').events.forEach((e) => this.handle(e, answerId));
  }

  /** Applies one event to the conversation. Public for tests. */
  handle(event: AgentSseEvent, answerId: string): void {
    const data = (event.data ?? {}) as Record<string, unknown>;
    switch (event.event) {
      case 'start':
        if (typeof data['thread_id'] === 'string') this.threadId.set(data['thread_id']);
        break;
      case 'delta':
        if (typeof data['text'] === 'string') {
          const delta = data['text'];
          this.messages.update((list) => list.map((m) => (m.id === answerId ? { ...m, text: m.text + delta } : m)));
        }
        break;
      case 'message':
        if (typeof data['text'] === 'string') this.patch(answerId, { text: data['text'] });
        break;
      case 'progress':
        this.progress.set(typeof data['label'] === 'string' ? data['label'] : null);
        break;
      case 'ui_action':
        this.ui.handle(data);
        break;
      case 'interrupt':
        if (data['type'] === 'portal_approval' && Array.isArray(data['actions'])) {
          const approval: AgentApproval = {
            interruptId: String(data['interrupt_id'] ?? ''),
            reason: typeof data['reason'] === 'string' ? data['reason'] : '',
            actions: (data['actions'] as AgentApprovalAction[]).filter((a) => a && typeof a.proposal_id === 'string'),
            state: 'pending',
          };
          // The agent's sentence is the card's "why" — drop it from the answer bubble.
          this.messages.update((list) => [
            ...list.filter((m) => !(m.id === answerId && (!m.text || m.text === approval.reason))),
            { id: this.nextId(), role: 'agent', text: '', approval },
          ]);
        }
        break;
      case 'action':
        this.messages.update((list) => [
          ...list.filter((m) => !(m.id === answerId && !m.text)),
          {
            id: this.nextId(),
            role: 'agent',
            text: '',
            trace: {
              ok: data['ok'] === true,
              actionId: typeof data['action_id'] === 'string' ? data['action_id'] : null,
              summary: typeof data['summary'] === 'string' ? data['summary'] : '',
              uiTarget: typeof data['ui_target'] === 'string' ? data['ui_target'] : null,
              uiAnchor: typeof data['ui_anchor'] === 'string' ? data['ui_anchor'] : null,
              reason: typeof data['reason'] === 'string' ? data['reason'] : null,
            },
          },
          ...list.filter((m) => m.id === answerId && !m.text),
        ]);
        break;
      case 'error':
        this.fail(answerId, data['message'] === 'agent_busy' ? 'busy' : 'failed');
        break;
      default:
        break;
    }
  }

  /** „Zeigen" on an approval card or a trace line: goes to the object (for a
   *  setting: its page and tab) and points at it. */
  show(target: string, anchor?: string | null): boolean {
    return this.ui.handle({ action: 'show', target, anchor });
  }

  private fail(answerId: string, kind: AgentChatError): void {
    this.error.set(kind);
    this.messages.update((list) => list.filter((m) => !(m.id === answerId && !m.text)));
  }

  private patch(id: string, change: Partial<AgentChatMessage>): void {
    this.messages.update((list) => list.map((m) => (m.id === id ? { ...m, ...change } : m)));
  }

  private async createThread(): Promise<string> {
    const res = await firstValueFrom(this.http.post<{ data: AgentThread }>(`${this.base}/threads`, {}));
    const thread = res.data;
    this.threadId.set(thread.id);
    this.threads.update((list) => [thread, ...list]);
    return thread.id;
  }

  private async accessToken(): Promise<string> {
    // Refreshes an expired session first — fetch() bypasses the HTTP interceptor.
    await firstValueFrom(this.nbAuth.isAuthenticatedOrRefresh());
    const token = await firstValueFrom(this.nbAuth.getToken());
    return token?.getValue() ?? '';
  }

  private nextId(): string {
    this.counter += 1;
    return `m${this.counter}`;
  }
}
